/**
 * Common error layout for errors that get displayed on the elemental overpanel or blank pages
 * Use this inside of the Overpanel component:
 *
 * <Overpanel>
 *   <ErrorCommon />
 * </Overpanel>
 *
 * or use it wherever :)
 */

import PropTypes from 'prop-types'

import React from 'react'
import { get } from 'lodash'
import glamorous from 'glamorous'
import { StyleSheet, css } from 'lib/paypalphrodite'
import content from 'pp-react-l10n'
import { getFlowType } from '../../transfer/utils/url-utils'
import { HeadingText, BodyText, Button } from '@paypalcorp/pp-react'
import { SUMMARY_PAGE_URL, TRANSFER_BASE_URL } from '../constants'
import { mobileAndSmaller } from '../../lib/styles/media-queries'
import { tracking } from '../../lib/analytics/mParticle'
import Events from 'lib/analytics/eventTags'

import serverProps from 'transfer/utils/serverProps'

import { LINK_STYLES } from '../../lib/styles/styles'
import { ENV_TYPE } from '../../../../lib/constants'

const CDN_HOST_NAME = get(serverProps, 'cdnHostName')

const i18n = content('transfer/guestLogin/errors')

// Currently just used for getting pisp redirect content
// TODO: Shall we move this content elsewhere? Possible content may change.

const i18nSuccess = content('transfer/send/success')
const i18nPreview = content('transfer/page/preview')
const i18nError = content('transfer/send/error')

const vxOrange = '#f8981f'
const vxRed = '#c72e2e'
const vxPalBlue = '#009cde'

const ERROR_ILLUSTRATIONS = {
  INFO: `${CDN_HOST_NAME}/paypal-ui/illustrations/focused/svg/empty-warning.svg`, // TODO replace with PPUI illustration once ready
  WARNING: `${CDN_HOST_NAME}/paypal-ui/illustrations/focused/svg/empty-warning.svg`,
  CRITICAL: `${CDN_HOST_NAME}/paypal-ui/illustrations/focused/svg/empty-critical.svg`,
}

export const statusStyles = {
  WARNING: {
    vxClass: 'icon-attention-large',
    color: vxOrange,
  },
  CRITICAL: {
    vxClass: 'icon-critical-large',
    color: vxRed,
  },
  INFO: {
    vxClass: 'icon-info-large',
    color: vxPalBlue,
  },
}

const IllustrationContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
})
const IllustrationWrapper = glamorous.div({
  width: '9rem',
})

const StyledButton = glamorous.button({
  margin: '25px 0',
  [mobileAndSmaller]: {
    width: '100%',
  },
})

const RedirectWrapper = glamorous.p({
  padding: '2rem 0.5rem',
})
const SelfHelpWrapper = glamorous.p({
  padding: '0 10px',
  '& a': {
    ...LINK_STYLES,
  },
})

const MessageWrapper = glamorous.div({
  margin: '30px 0',
  '& a': {
    ...LINK_STYLES,
  },
})

class ErrorCommon extends React.Component {
  static propTypes = {
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    status: PropTypes.oneOf(['CRITICAL', 'WARNING', 'INFO']),
    header: PropTypes.oneOf(['DEFAULT', 'SENDING_LIMIT', 'CUSTOM_ERROR']),
    customErrorCode: PropTypes.string,
    rawErrorCode: PropTypes.string,
    env: PropTypes.string,
    hideServiceMessage: PropTypes.bool,
    handleBackButton: PropTypes.func,
    handleLetsTryAgainButton: PropTypes.func,
    serviceTextKey: PropTypes.string,
  }

  static defaultProps = {
    status: 'WARNING',
    headerContent: 'DEFAULT',
  }

  componentDidMount() {
    this.recordErrorEvent()
  }

  handleBackButtonClick = () => {
    const { handleBackButton } = this.props
    if (handleBackButton) {
      handleBackButton()
    }
  }

  handleLetsTryAgainClick = () => {
    const { handleLetsTryAgainButton } = this.props
    if (handleLetsTryAgainButton) {
      handleLetsTryAgainButton()
    }
  }

  recordErrorEvent() {
    const { rawErrorCode, message } = this.props

    let errorDescription = get(
      message,
      'props.dangerouslySetInnerHTML.__html',
      null
    )

    if (typeof message === 'string') {
      errorDescription = message
    }

    tracking(
      Events.p2p_failed_customer_interaction_occurred({
        ...(errorDescription && { error_description: errorDescription }),
        error_code: rawErrorCode,
      })
    )
  }

  render() {
    const { status, env } = this.props
    const isPisp = env === ENV_TYPE.PISP
    const amexScreen =
      this.props.customErrorCode === 'amexInsufficientFunds' ||
      this.props.customErrorCode === 'amexSuspectedFraud'
    const showSuccessRedirect = isPisp === true && getFlowType() === 'request'
    const styles = StyleSheet.create({
      main: {
        textAlign: 'center',
      },
      icon: {
        display: 'inline-block',
        lineHeight: '3.8rem', // so the element actually takes up the size of the icon, and margins look right
        ':before': {
          color: statusStyles[status].color,
        },
      },
      copy: {
        margin: '30px 0',
        font: '400 15px/24px PayPal-Sans, sans-serif',
        letterSpacing: '0.375px',
      },
      redirectMsg: {
        padding: '2rem 0.5rem',
        textAlign: 'center',
        fontWeight: '400',
        fontSize: '1.2rem',
        fontFamily: 'PayPal-Sans, sans-serif',
      },
      doneButton: {
        width: '15rem',
        margin: '25px 0',
      },
    })

    const headerContent = {
      DEFAULT: i18n('content.header'),
      SENDING_LIMIT: i18n('content.headerSendingLimit'),
      CUSTOM_ERROR: i18nError('error.' + this.props.customErrorCode),
    }

    return (
      <div className={css(styles.main)}>
        <IllustrationContainer>
          <IllustrationWrapper>
            <img src={ERROR_ILLUSTRATIONS[status]} alt="" />
          </IllustrationWrapper>
        </IllustrationContainer>
        <HeadingText size="sm" as="h2">
          {headerContent[this.props.header]}
        </HeadingText>
        <MessageWrapper>
          <BodyText data-nemo="error-message-common">
            {this.props.message}
          </BodyText>
        </MessageWrapper>
        {!amexScreen &&
          (this.props.hideServiceMessage || (
            <BodyText as={SelfHelpWrapper}>
              {i18n.html(this.props.serviceTextKey || 'content.service', {
                helpLink: '/selfhelp/home',
                transferHomePage: TRANSFER_BASE_URL,
                summaryPage: SUMMARY_PAGE_URL,
              })}
            </BodyText>
          ))}
        {showSuccessRedirect && (
          <BodyText as={RedirectWrapper} data-testid="pisp-redirect-msg">
            {i18nSuccess.html('success.redirect')}
          </BodyText>
        )}
        {amexScreen && (
          <Button as={StyledButton} onClick={this.handleLetsTryAgainClick}>
            {i18nError('error.button.letsTryAgain')}
          </Button>
        )}
        {isPisp && (
          <Button as={StyledButton} onClick={this.handleBackButtonClick}>
            {i18nPreview('link.back')}
          </Button>
        )}
      </div>
    )
  }
}

export default ErrorCommon
